import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getCustomReport, exportCsv } from 'services/api/AdebifyApi';
import commonSelectors from 'store/common/selectors';
import { commonActions } from 'store/common';
import settingsSelector from 'store/settings/selectors';
import types from './types';
import actions from './actions';
import selectors from './selectors';

function* fetchReport() {
  yield put(actions.requestStart());

  try {
    const settings = yield select(selectors.getSettings);
    const sort = yield select(selectors.getSort);
    const pagination = yield select(selectors.getPagination);
    const tab = yield select(settingsSelector.getTab);
    const channel = tab;
    const period = yield select(commonSelectors.periodSelector);

    const startDate = period?.start?.format('YYYY-MM-DD') || '';
    const endDate = period?.end?.format('YYYY-MM-DD') || '';

    // Guard: don't send if required params are missing
    if (!startDate || !endDate) {
      yield put(actions.requestFailed('期間を選択してください'));
      return;
    }
    if (
      settings.mode !== 'cv_attributes' &&
      settings.rowDimensions.length === 0
    ) {
      yield put(actions.requestFailed('集計軸（行）を選択してください'));
      return;
    }

    const params = {
      start_date: startDate,
      end_date: endDate,
      mode: settings.mode,
      channel,
      limit: pagination.itemsPerPage,
      offset: (pagination.currentPage - 1) * pagination.itemsPerPage,
    };

    // Normal/pivot mode params (not needed for cv_attributes)
    if (settings.mode !== 'cv_attributes') {
      params.dimensions = settings.rowDimensions;
      // Pivot mode: send only the first metric (single metric mode)
      params.metrics = settings.mode === 'pivot'
        ? [settings.metrics[0]]
        : settings.metrics;
      params.axis = settings.axis;
      params.product_axis = settings.productAxis;
      params.sort_by = sort.field;
      params.sort_order = sort.direction;

      // Determine metric_view from selected metrics
      const ltvMetricKeys = new Set([
        'f1_revenue', 'new_aov', 'initial_gross_profit',
        'f2_conversion_rate', 'avg_order_count',
        'ltv_30d_total', 'ltv_30d', 'ltv_30d_growth_rate',
        'ltv_60d_total', 'ltv_60d', 'ltv_60d_growth_rate',
        'ltv_90d_total', 'ltv_90d', 'ltv_90d_growth_rate',
        'ltv_180d_total', 'ltv_180d', 'ltv_180d_growth_rate',
        'ltv_365d_total', 'ltv_365d', 'ltv_365d_growth_rate',
        'total_ltv', 'ltv', 'ltv_growth_rate',
        'redistributed_f1_revenue', 'redistributed_total_ltv',
        'ncpa', 'new_roas', 'ltv_roas',
        'new_troas', 'ltv_troas', 'initial_poas', 'ltv_poas',
      ]);
      const hasLtvMetric = settings.metrics.some((m) => ltvMetricKeys.has(m));
      params.metric_view = hasLtvMetric ? 'ltv' : 'all';
    }

    // CV attributes mode: send display column selections
    if (settings.mode === 'cv_attributes') {
      params.display_columns = settings.cvDisplayColumns;
      // When filters are active, send touch_point_targets so the backend
      // filters CVs based on which contact points match (接触タイミング).
      if (Object.keys(settings.filters).length > 0 && settings.touchPointTargets?.length > 0) {
        params.touch_point_targets = settings.touchPointTargets;
      }
    }

    // Add column dimensions for pivot mode
    if (settings.mode === 'pivot' && settings.columnDimensions.length > 0) {
      params.column_dimensions = settings.columnDimensions;
    }

    // Add filters if present
    if (Object.keys(settings.filters).length > 0) {
      params.filters = settings.filters;
    }

    const response = yield call(getCustomReport, params);
    yield put(
      actions.fetchReportSucceeded({
        data: response.data.data || response.data.table_data,
        metadata: response.data.meta || {},
      })
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || error?.message || 'Unknown error';
    yield put(actions.requestFailed(errorMessage));
  }
}

function* handleDownloadCsv() {
  try {
    yield put(commonActions.setDownloadNotify('CSVファイルを作成しています...'));

    const settings = yield select(selectors.getSettings);
    const tab = yield select(settingsSelector.getTab);
    const channel = tab;
    const period = yield select(commonSelectors.periodSelector);

    const startDate = period?.start?.format('YYYY-MM-DD') || '';
    const endDate = period?.end?.format('YYYY-MM-DD') || '';

    const exportParams = {
      report_type: 'custom_report',
      start_date: startDate,
      end_date: endDate,
      mode: settings.mode,
      dimensions: settings.rowDimensions,
      metrics: settings.mode === 'pivot'
        ? [settings.metrics[0]]
        : settings.metrics,
      axis: settings.axis,
      product_axis: settings.productAxis,
    };
    if (channel) {
      exportParams.channel = channel;
    }
    if (settings.mode === 'cv_attributes') {
      exportParams.display_columns = settings.cvDisplayColumns;
    }
    if (settings.mode === 'pivot' && settings.columnDimensions.length > 0) {
      exportParams.column_dimensions = settings.columnDimensions;
    }
    if (Object.keys(settings.filters).length > 0) {
      exportParams.filters = settings.filters;
    }

    const response = yield call(exportCsv, exportParams);

    // Response contains a presigned S3 download URL
    const downloadUrl = response.data?.download_url;
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    yield put(commonActions.setDownloadNotify('CSVファイルを作成しました'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('CSV export failed:', error);
    yield put(commonActions.setDownloadNotify(''));
  }
}

export default function* adebifyCustomReportOperations() {
  yield takeLatest(types.FETCH_REPORT, fetchReport);
  yield takeLatest(types.DOWNLOAD_CSV, handleDownloadCsv);
}

// Exported for saga payload contract tests.
export { fetchReport, handleDownloadCsv };
